import { useState, useMemo, useEffect } from 'react'
import {
  useNavigate,
  useParams,
  useLocation,
  Outlet,
} from 'react-router-dom-v5-compat'

import {
  Button,
  Link,
  PlusIcon,
  toast,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { TagEditDialog } from '~publish/components/TagEditDialog/TagEditDialog'
import { useAccount } from '~publish/contexts/AccountContext'
import type { Tag } from '~publish/gql/graphql'
import { useTags } from '~publish/hooks/useTags'
import UserEntity from '~publish/legacy/user/UserEntity'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'

import { NewSettingsPageLayout } from '../components/NewSettingsPageLayout'
import { TagsEmptyState } from './components/TagsEmptyState'
import { TagsErrorState } from './components/TagsErrorState'
import { TagsList } from './components/TagsList'
import { TagsLoadingSkeleton } from './components/TagsLoadingSkeleton'

const TagsSubtitle = (): JSX.Element => (
  <>
    Tags are visible to everyone in your organization.{' '}
    <Link
      href={`https://support.buffer.com/article/585-creating-and-managing-campaigns?${HC_UTM_PARAMS}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more
    </Link>
  </>
)

export const Tags = (): JSX.Element => {
  const isMobile = useIsMobileScreenSize()
  const account = useAccount()
  const isAdmin = UserEntity.isAdmin(account)
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ tagId?: string }>()

  const [searchFilter, setSearchFilter] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)

  const {
    tags,
    loading: loadingTags,
    error: errorLoadingTags,
    createTag,
    updateTag,
    hasCreatePermission,
  } = useTags()

  const filteredTags = useMemo(() => {
    if (!searchFilter.trim()) return tags
    return tags.filter((tag) =>
      tag.name.toLowerCase().includes(searchFilter.toLowerCase()),
    )
  }, [tags, searchFilter])

  // Handle URL-based dialog opening
  useEffect(() => {
    const currentPath = location.pathname

    if (currentPath.endsWith('/new')) {
      setCreateDialogOpen(true)
    } else if (params.tagId && currentPath.includes('/edit')) {
      setEditDialogOpen(true)
    }
  }, [location.pathname, params.tagId])

  const isEditPath = useMemo(
    () => location.pathname.includes('/edit'),
    [location.pathname],
  )

  useEffect(() => {
    if (params.tagId && isEditPath && tags.length > 0) {
      const tag = tags.find((t) => t.id === params.tagId)
      if (tag) {
        setEditingTag(tag)
      }
    }
  }, [params.tagId, tags, isEditPath])

  // Dialog handlers
  const handleCreateDialogClose = (open: boolean) => {
    setCreateDialogOpen(open)
    if (!open) {
      navigate('/settings/tags', { replace: true })
    }
  }

  const handleEditDialogClose = (open: boolean) => {
    setEditDialogOpen(open)
    if (!open) {
      setEditingTag(null)
      navigate('/settings/tags', { replace: true })
    }
  }

  const handleCreateTagClick = () => {
    navigate('/settings/tags/new')
  }

  const handleEditTagClick = (tag: Tag) => {
    navigate(`/settings/tags/${tag.id}/edit`)
  }

  const handleCreateTagSubmit = async (tag: Tag): Promise<void> => {
    try {
      await createTag({ name: tag.name, color: tag.color })
    } catch {
      toast.error('Failed to create tag. Please try again later.')
    }
  }

  const handleEditTagSubmit = async (tag: Tag): Promise<void> => {
    try {
      await updateTag({ id: tag.id, name: tag.name, color: tag.color })
    } catch {
      toast.error('Failed to update tag. Please try again later.')
    }
  }

  // Action button for creating new tags
  const newTagButton = isAdmin && hasCreatePermission && (
    <Button onClick={handleCreateTagClick}>
      {!isMobile && <PlusIcon />}
      {'New Tag'}
    </Button>
  )

  // View state logic
  const showEmptyState = !loadingTags && !errorLoadingTags && tags.length === 0
  const showTagsList = !loadingTags && !errorLoadingTags && tags.length > 0

  return (
    <>
      <NewSettingsPageLayout
        title="Tags"
        subtitle={<TagsSubtitle />}
        actions={showTagsList ? newTagButton : null}
      >
        {loadingTags && <TagsLoadingSkeleton />}
        {errorLoadingTags && <TagsErrorState />}
        {showEmptyState && (
          <TagsEmptyState
            isAdmin={isAdmin}
            hasCreatePermission={hasCreatePermission}
            onCreateClick={handleCreateTagClick}
          />
        )}
        {showTagsList && (
          <TagsList
            searchFilter={searchFilter}
            onSearchChange={setSearchFilter}
            filteredTags={filteredTags}
            isAdmin={isAdmin}
            onEditTag={handleEditTagClick}
          />
        )}
      </NewSettingsPageLayout>

      {/* Create Tag Dialog */}
      <TagEditDialog
        open={createDialogOpen}
        onOpenChange={handleCreateDialogClose}
        onSubmit={handleCreateTagSubmit}
      />

      {/* Edit Tag Dialog */}
      <TagEditDialog
        open={editDialogOpen}
        onOpenChange={handleEditDialogClose}
        onSubmit={handleEditTagSubmit}
        tag={editingTag || undefined}
      />

      <Outlet />
    </>
  )
}

export default Tags
